import { ActivityIndicator, Pressable } from "react-native";
import { Text, View } from "@src/components";
import { formatCalendarDate } from "@src/utils/format-date";
import { useRoadSenseWorkbookDownload } from "../hooks/useRoadSenseWorkbookDownload";

export interface DownloadWorkbookButtonProps {
  readonly reportId: string;
  /** This site's `.xlsx` key; without one there is nothing to download. */
  readonly workbookS3Key: string | undefined;
  /** The last day the stored workbook covers, shown under the label. */
  readonly workbookPeriodEnd: string | undefined;
}

/** The site report's "Download workbook" button, shown when `roadsense_export` is off. */
export function DownloadWorkbookButton({ reportId, workbookS3Key, workbookPeriodEnd }: DownloadWorkbookButtonProps) {
  const { download, isDownloading, error } = useRoadSenseWorkbookDownload(reportId, workbookS3Key);
  if (!workbookS3Key) return null;
  const coversTo = workbookPeriodEnd !== undefined ? formatCalendarDate(workbookPeriodEnd) : "";

  return (
    <View gap="xs" alignItems="flex-end">
      <Pressable
        onPress={isDownloading ? undefined : download}
        accessibilityRole="button"
        accessibilityState={{ disabled: isDownloading }}
        style={{ paddingVertical: 8, paddingHorizontal: 16 }}
      >
        {isDownloading ?
          <View flexDirection="row" gap="sm" alignItems="center">
            <ActivityIndicator />
            <Text textStyle="xs/regular" color="textMuted">
              Preparing download…
            </Text>
          </View>
        : <View alignItems="flex-end">
            <Text textStyle="xs/semibold" color="text">
              Download workbook
            </Text>
            {coversTo ?
              <Text textStyle="xs/regular" color="textMuted">
                (to {coversTo})
              </Text>
            : null}
          </View>
        }
      </Pressable>
      {error ?
        <Text textStyle="xs/regular" color="textMuted">
          Download failed. Please try again.
        </Text>
      : null}
    </View>
  );
}
